<template>
  <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50" @click.self="$emit('close')"
    @keydown.esc="$emit('close')" tabindex="-1">
    <div class="w-full max-w-2xl px-5 pb-2 bg-white rounded-lg shadow-xl max-h-[90vh] overflow-y-auto">
      <div class="sticky top-0 z-10">
        <div class="flex justify-between items-center bg-gray-300 -mx-5 px-2 py-2">
          <h3 class="text-xl font-semibold text-secondary-800">CSV Format Examples</h3>
          <button @click="$emit('close')" class="text-red-700 hover:text-red-900 mr-2">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="flex text-sm bg-blue-100 p-2 pl-2 -mx-5 text-blue-800 font-semibold">
          For more information, see:
          <a href="/agencies/help#csv-upload" target="_blank"
            class="px-2 text-blue-600 flex italic hover:text-blue-800">
            <span class="underline italic">Help Center (CSV Upload Format)</span>
            <i class="fas fa-external-link-alt fa-xs self-start pt-1 ml-1 text-blue-600"></i>
          </a>
        </div>
      </div>

      <div class="space-y-2">
        <div>
          <h4 class="font-medium text-sage mb-2 text-sm mt-2">With Headers: <span
              class="text-xs italic text-gray-500">(Can be in any order*)</span></h4>
          <div class="">
            <table class="min-w-full border border-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-3 py-2 border text-sm">Name</th>
                  <th class="px-3 py-2 border text-sm">Document Type</th>
                  <th class="px-3 py-2 border text-sm">Document Date</th>
                  <th class="px-3 py-2 border text-sm">Comments</th>
                </tr>
              </thead>
              <tbody class="text-sm">
                <tr>
                  <td class="px-3 py-2 border">John Smith</td>
                  <td class="px-3 py-2 border">SOC</td>
                  <td class="px-3 py-2 border">01/15/2024</td>
                  <td class="px-3 py-2 border italic text-gray-500"></td>
                </tr>
                <tr>
                  <td class="px-3 py-2 border">Jane Doe</td>
                  <td class="px-3 py-2 border">Start of Care</td>
                  <td class="px-3 py-2 border">Feb 20 2024</td>
                  <td class="px-3 py-2 border italic text-gray-500">Optional comments here</td>
                </tr>
              </tbody>
            </table>
            <div class="text-xs italic text-gray-500 mt-1">*Must include "Patient/Name", "Type", and "Date" as keywords
              to detect the order. All case-insensitive.</div>
          </div>
        </div>

        <div>
          <h4 class="font-medium text-sage mb-2 text-sm">Without Headers:</h4>
          <div class="">
            <table class="min-w-full border border-gray-200">
              <tbody class="text-sm">
                <tr>
                  <td class="px-3 py-2 border">Mary Wilson</td>
                  <td class="px-3 py-2 border">Recert</td>
                  <td class="px-3 py-2 border">3/1/24</td>
                  <td class="px-3 py-2 border italic text-gray-500"></td>
                </tr>
                <tr>
                  <td class="px-3 py-2 border">Tom Brown</td>
                  <td class="px-3 py-2 border">FU</td>
                  <td class="px-3 py-2 border">March 1 2024</td>
                  <td class="px-3 py-2 border italic text-gray-500">Optional comments here</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="text-sm text-gray-600 space-y-3">
          <div>
            <p class="font-medium mb-1">Document Types (case insensitive):</p>
            <div class="ml-2 text-xs">
              <div v-for="(aliases, type) in groupedDocTypes" :key="type" class="odd:bg-gray-50">
                <div class="p-1">
                  <span class="font-medium">{{ type }}:</span> {{ aliases.join(', ') }}
                </div>
              </div>
            </div>
          </div>

          <div>
            <p class="font-medium mb-1">Date Formats:</p>
            <div class="ml-2">
              <div 
                class="grid grid-cols-2"
                style="grid-template-rows: repeat(auto-fill, minmax(0, 1fr));"
              >
                <template v-for="(format, index) in dateFormats">
                  <div 
                    :key="index"
                    :class="{ 'bg-gray-50': Math.floor(index / 2) % 2 === 0 }"
                    class="p-1 text-xs"
                  >
                    <span class="block font-semibold">
                      {{ format }}
                      <span class="italic text-gray-500 font-normal">
                        ({{ exampleDate(format) }})
                      </span>
                    </span>
                  </div>
                </template>
              </div>
            </div>
          </div>

          <div class="text-xs border-t border-gray-200 pt-2">
            <p class="font-medium mb-1">Notes:</p>
            <ul class="ml-2 space-y-1 list-disc list-inside">
              <li>Headers are optional - detected automatically</li>
              <li>Comments are optional</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';

export default {
  props: {
    show: {
      type: Boolean,
      default: false
    },
    dateFormats: {
      type: Array,
      required: true
    },
    documentTypeMap: {
      type: Object,
      required: true
    }
  },

  computed: {
    groupedDocTypes() {
      const groups = {};
      Object.entries(this.documentTypeMap).forEach(([input, output]) => {
        if (!groups[output]) {
          groups[output] = [];
        }
        groups[output].push(input);
      });
      return groups;
    }
  },

  mounted() {
    document.addEventListener('keydown', this.handleKeydown)
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown)
  },

  methods: {
    handleKeydown(e) {
      if (this.show && e.key === 'Escape') {
        this.$emit('close')
      }
    },

    exampleDate(format) {
      return moment().format(format);
      const date = new Date();
      const month = date.getMonth() + 1;
      const day = date.getDate();
      const year = date.getFullYear();
      const shortYear = year.toString().slice(-2);
      
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];

      switch (format) {
        case 'YYYY-MM-DD':
          return `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
        case 'MM/DD/YYYY':
          return `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`;
        case 'MM/DD/YY':
          return `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${shortYear}`;
        case 'M/D/YYYY':
          return `${month}/${day}/${year}`;
        case 'M/D/YY':
          return `${month}/${day}/${shortYear}`;
        case 'MM-DD-YYYY':
          return `${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}-${year}`;
        case 'MM-DD-YY':
          return `${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}-${shortYear}`;
        case 'MMM DD YYYY':
          return `${monthNames[month-1].slice(0,3)} ${day.toString().padStart(2, '0')} ${year}`;
        case 'MMM DD YY':
          return `${monthNames[month-1].slice(0,3)} ${day.toString().padStart(2, '0')} ${shortYear}`;
        case 'MMMM DD YYYY':
          return `${monthNames[month-1]} ${day.toString().padStart(2, '0')} ${year}`;
        case 'MMMM DD YY':
          return `${monthNames[month-1]} ${day.toString().padStart(2, '0')} ${shortYear}`;
        default:
          return date.toLocaleDateString('en-US');
      }
    }
  }
}
</script>

<style scoped>
/* Hide scrollbar for Chrome, Safari and Opera */
/* .overflow-y-auto::-webkit-scrollbar {
  display: none;
} */

/* Hide scrollbar for IE, Edge and Firefox */
/* .overflow-y-auto {
  -ms-overflow-style: none; 
  scrollbar-width: none;  
} */
</style>